import React from 'react';
import { partition } from 'lodash';
import { UseLinks, TrackEvent } from '../../types';
import { useConfirm } from '../../hooks/useConfirm';
import { Checkbox } from '../Common/Checkbox';
import { Confirm } from './Confirm';
import { LinkList } from './LinkList';
import './index.scss';

interface LinksProps {
  links: UseLinks;
  onTrackEvent: TrackEvent;
}

export const Links: React.FC<LinksProps> = ({
  links,
  links: { list, update, remove, isArchivedVisible, toggleIsArchivedVisible },
  onTrackEvent,
}) => {
  const confirm = useConfirm();
  const { open: openConfirm } = confirm;
  const [hiddenLinks, visibleLinks] = partition(list, link => !!link.archived_at);

  const onArchivedToggle = React.useCallback(
    (id: number, isArchived: boolean) => {
      if (isArchived) {
        update(id, { archived: false });
        return;
      }

      openConfirm({
        successMessage: 'Link is hidden',
        onConfirm: () => update(id, { archived: true }),
      });
    },
    [openConfirm, update],
  );

  const onRemove = React.useCallback(
    (id: number) => {
      openConfirm({ successMessage: 'Link is removed', onConfirm: () => remove(id) });
    },
    [openConfirm, remove],
  );

  const listProps = {
    links,
    onTrackEvent,
    onArchivedToggle,
    onRemove,
  };

  return (
    <div className="links flex-one flex-box-column">
      <h3 className="flex-auto">Recently generated links</h3>
      <Checkbox label="Show hidden links" isChecked={isArchivedVisible} onChange={toggleIsArchivedVisible} />
      <div className="flex-one scrollable">
        {isArchivedVisible && <LinkList {...listProps} title="Hidden Links" list={hiddenLinks} testId="hidden-links" />}
        <LinkList
          {...listProps}
          title={isArchivedVisible ? 'Other Links' : undefined}
          list={visibleLinks}
          testId="other-links"
        />
      </div>
      <Confirm confirm={confirm} />
    </div>
  );
};
